import React, { useState, useEffect, useRef } from 'react'
import './CorporateTwoCategory.scss'
import { useCollection } from '../../../../../firebase-files/hooks/useCollection'
import { useNavigate } from 'react-router-dom'

const CorporateTwoCategory = () => {
  const navigate = useNavigate();
  const [localCategories, setLocalCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(5);

  // refs
  const containerRef = useRef(null); // scrollable container
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  const { documents: categories, error: categoriesError } = useCollection(
    "m_categories",
    ["status", "==", "active"]
  );

  // Load categories
  useEffect(() => {
    if (categories) {
      setLocalCategories(categories);
      setLoading(false);
      setCurrentSlide(0);
      // scroll to start
      if (containerRef.current) containerRef.current.scrollLeft = 0;
    }
  }, [categories]);

  // Responsive items per view
  useEffect(() => {
    const calcItems = () => {
      const w = window.innerWidth;
      if (w <= 480) setItemsPerView(3);
      else if (w <= 768) setItemsPerView(3);
      else if (w <= 1024) setItemsPerView(4);
      else setItemsPerView(5);
    };

    calcItems();
    window.addEventListener('resize', calcItems);
    return () => window.removeEventListener('resize', calcItems);
  }, []);

  // Ensure currentSlide is within bounds when itemsPerView or categories change
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(localCategories.length / itemsPerView));
    if (currentSlide >= pages) {
      const newSlide = pages - 1;
      setCurrentSlide(newSlide);
      if (containerRef.current) {
        containerRef.current.scrollTo({ left: newSlide * containerRef.current.clientWidth, behavior: 'smooth' });
      }
    } else {
      if (containerRef.current) {
        containerRef.current.scrollTo({ left: currentSlide * containerRef.current.clientWidth, behavior: 'smooth' });
      }
    }
  }, [itemsPerView, localCategories.length]);

  const getPages = () => Math.max(1, Math.ceil(localCategories.length / itemsPerView));

  const goToSlide = (index) => {
    const pages = getPages();
    const target = Math.max(0, Math.min(index, pages - 1));
    if (!containerRef.current) return;
    containerRef.current.scrollTo({ left: target * containerRef.current.clientWidth, behavior: 'smooth' });
    setCurrentSlide(target);
  };

  const nextSlide = () => goToSlide(currentSlide >= getPages() - 1 ? 0 : currentSlide + 1);
  const prevSlide = () => goToSlide(currentSlide <= 0 ? getPages() - 1 : currentSlide - 1);

  // Sync currentSlide when user scrolls (e.g. touch swipe)
  const handleScroll = () => {
    if (!containerRef.current) return;
    const pageWidth = containerRef.current.clientWidth;
    const newSlide = Math.round(containerRef.current.scrollLeft / pageWidth);
    if (newSlide !== currentSlide) setCurrentSlide(newSlide);
  };

  // Touch swipe fallback (keeps behaviour consistent on non-scroll snapping devices)
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const start = touchStartRef.current;
    const end = touchEndRef.current;
    if (start == null || end == null) return;
    const distance = start - end;
    const minSwipe = 50;
    if (Math.abs(distance) < minSwipe) return;
    if (distance > 0) nextSlide();
    else prevSlide();
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  if (loading) return (
    <div className="corporate-two-category">
      Loading...
    </div>
  );

  return (
    <div className="corporate-two-category">
      <div className="corporate-two-category-header">
        <h2>Categories</h2>
        {/* <button onClick={() => navigate('/categories')}>See All</button> */}
      </div>

      {/* Make the container the scrollable element */}
      <div
        className="corporate-two-category-container"
        ref={containerRef}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="category-slider">
          {localCategories.map((category) => (
            <div
              key={category.id}
              className="category-item"
              // enforce exact item width per viewport using inline styles
              style={{ flex: `0 0 ${100 / itemsPerView}%`, maxWidth: `${100 / itemsPerView}%`, boxSizing: 'border-box' }}
              onClick={() => navigate(`/category/${category.id}`)}
            >
              <div className="category-image">
                <img src={category.categoryImgUrl} alt={category.category} />
              </div>
              <h3>{category.category}</h3>
            </div>
          ))}
        </div>
      </div>

      {/* Show navigation buttons only on desktop */}
      <div className="slider-controls desktop-only">
        <button onClick={prevSlide} className="prev-btn" aria-label="Previous categories">
          <span>←</span>
        </button>
        <div className="dots" aria-hidden>
          {Array.from({ length: getPages() }).map((_, i) => (
            <button
              key={i}
              className={`dot ${i === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>
        <button onClick={nextSlide} className="next-btn" aria-label="Next categories">
          <span>→</span>
        </button>
      </div>
    </div>
  )
}

export default CorporateTwoCategory
